/* eslint-disable no-nested-ternary */
import { openModal } from "action/modal/openModal";
import { setDeliveryMethod } from "action/shopping-cart/setDeliveryMethod";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import classnames from "classnames";
import DeliveryInStorePickupIcon from "component/design-system/badge/DeliveryInStorePickupIcon";
import DeliveryRapidShippingIcon from "component/design-system/badge/DeliveryRapidShippingIcon";
import DeliveryShippingIcon from "component/design-system/badge/DeliveryShippingIcon";
import { Card } from "component/design-system/shared-styles/StyledCard";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import { DELIVERY_METHODS } from "constant/action-constants";
import { RootState } from "interfaces/generics.type";
import { AvailableDeliveryMethodsDetail } from "interfaces/session/ShoppingCart";
import PickUpOnlyWarning from "modules/product/product-delivery/PickUpOnlyWarning";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { compactShippingFee } from "util/helper/compactShippingFee";
import { currency } from "util/helper/currency";
import { getDeliveryMethodDetails } from "util/helper/getDeliveryMethodDetails";
import { isDeliveryMethodUnAvailable } from "util/helper/isDeliveryMethodUnAvailable";
import { isPickUpDeliveryAvailable } from "util/helper/isPickUpDeliveryAvailable";
import { isApparelProduct } from "util/product/apparel";
import { PRODUCT_PAGE_TYPES } from "util/product/product-constants";
import { isCheckoutPages, isShoppingCartPage } from "util/url/checkRouteInclude";
import DeliveryCounter from "../delivery-counter/DeliveryCounter";
import { isSurgeCostApplicable } from "../helper/isSurgeCostApplicable";
import { minimumShippingCost } from "../helper/minimumShippingCost";
import { PickupShippingInfo, RapidDeliveryInfo, ShippingDeliveryInfo } from "./DeliveryShippingInfo";
import EstimatedDeliveryDate from "./EstimatedDeliveryDate";
import styles from "./newDeliveryMethodSelector.module.scss";

interface IProps {
  product?: ICompactProduct;
  onChange: () => void;
  showOnlySelected: boolean;
  showOnlyAvailable?: boolean;
  postalCode: string;
  availableDeliveryMethodsDetails: AvailableDeliveryMethodsDetail[];
  className?: string;
}

const NewDeliveryMethodSelector = (props: IProps) => {
  const {
    onChange, showOnlySelected, postalCode, availableDeliveryMethodsDetails,
    product, showOnlyAvailable = false, className, compactOnMobile,
  } = props;

  const currentPageType = useSelector((state: RootState) => state.utils.currentPageType);
  const dispatch = useDispatch();
  const router = useRouter();
  const { locale, query, pathname } = router;

  const { t } = useTranslation(["shopping-cart", "common"]);

  const userCart = useSelector((store: RootState) => store.shoppingCart);

  const {
    availableItems, deliveryMethod, availableDeliveryMethodsForProducts,
  } = userCart;

  const { pickUpAvailable, deliveryAvailable } = isPickUpDeliveryAvailable(availableDeliveryMethodsDetails);
  const { shipmentDetails, deliveryDetails } = getDeliveryMethodDetails(availableDeliveryMethodsDetails) || {};
  const showShippingCostDetails = !!shipmentDetails?.baseShipmentCost;
  const showDeliveryCostDetails = !!deliveryDetails?.deliveryCost;
  const showCostDetailsLayout = showShippingCostDetails || showDeliveryCostDetails;

  // Math random number is used so that input has unique name
  // otherwise if we have multiple components in same page, it won't update the delivery method on selection
  // const name = fromSideMenu ? "sidebar-delivery" : inputName;

  function getItemsWithDeliveryMethod(type: string) {
    const updatedItems = availableItems.filter((eachItem) => eachItem.availableDeliveryMethods.includes(type));
    return updatedItems;
  }

  const handleOnChange = (type: any) => {
    if (showOnlyAvailable && !availableDeliveryMethodsForProducts.includes(type)) {
      return;
    }
    const updatedItems = getItemsWithDeliveryMethod(type);
    const shouldShowModel = updatedItems.length !== availableItems.length;
    if (shouldShowModel) {
      // open model
      dispatch(openModal("changeDeliveryMethodConfirmationModal", {
        updatedItems,
        deliveryMethodToSelect: type,
      }));
    } else {
      if (props.onChange) {
        onChange(true);
      }
      dispatch(setDeliveryMethod(type));
    }
  };

  const freeText = (
    <span className={`${styles.shipping_cost_label} ${styles.green_text}`}>
      {t("FREE")}
    </span>

  );

  const showPickupMethod = !showOnlySelected || (showOnlySelected && deliveryMethod === DELIVERY_METHODS.PICKUP);
  const showShippingMethod = !showOnlySelected || (showOnlySelected && deliveryMethod === DELIVERY_METHODS.SHIPPING);
  const showDeliveryMethod = !showOnlySelected || (showOnlySelected && deliveryMethod === DELIVERY_METHODS.DELIVERY);

  useEffect(() => {
    if (!pickUpAvailable && !deliveryAvailable && deliveryMethod !== DELIVERY_METHODS.SHIPPING && PRODUCT_PAGE_TYPES.includes(currentPageType)) {
      handleOnChange(DELIVERY_METHODS.SHIPPING);
    }
  }, [pickUpAvailable, deliveryAvailable, currentPageType]);
  if (!postalCode) {
    return null;
  }

  if (!pickUpAvailable && !deliveryAvailable) {
    return (
      <Card className={`d-flex flex-column p-0 ${styles.overflow_show}`}>
        <ShippingDeliveryInfo
          deliveryMethod={DELIVERY_METHODS.SHIPPING}
          component={(
            <div>
              <EstimatedDeliveryDate
                deliveryMethod={DELIVERY_METHODS.SHIPPING}
                availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                textInGray
              />
            </div>
          )}
          availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
        />
      </Card>

    );
  }

  let deliveryContainerClass = "";

  if (isCheckoutPages(pathname, query) || (product && isApparelProduct(product))) {
    deliveryContainerClass = styles.shopping_cart_container;
  } else if (isShoppingCartPage(pathname)) {
    deliveryContainerClass = styles.shopping_cart_container_cart_page;
  }

  const productPageContainerClass = !(isShoppingCartPage(pathname) || isCheckoutPages(pathname, query)) ? styles.product_page_cart_container : "";

  return (
    <Card className={`d-flex flex-column p-0 ${styles.overflow_show}`}>
      <div
        className={classnames(`delivery-method-container ${styles.wrapper_container} ${deliveryContainerClass} ${productPageContainerClass}`, {
          [styles.mobile_compact_delivery_wrapper_container]: compactOnMobile,
          [styles.delivery_method_row_layout]: !!deliveryContainerClass,
        })}
      >
        <HideIf show={showPickupMethod}>
          {pickUpAvailable && (
            <>
              <div
                className={classnames(`${styles.delivery_method_container} ${styles.pointer} ${className}`, {
                  [styles.active]: deliveryMethod === DELIVERY_METHODS.PICKUP,
                  [styles.disabled]: showOnlyAvailable && !availableDeliveryMethodsForProducts.includes(DELIVERY_METHODS.PICKUP),
                  [styles.mobile_compact_delivery_method]: compactOnMobile,
                  [styles.checkout_delivery_method_container]: isCheckoutPages(pathname, query),
                  [styles.delivery_method_container_with_cost_details]: showCostDetailsLayout,
                })}
                onClick={() => handleOnChange(DELIVERY_METHODS.PICKUP)}
              >
                <div className={styles.icon_wrapper}>
                  <DeliveryInStorePickupIcon width={40} height={40} />
                </div>
                <div className={styles.shipping_cost_label_container}>
                  <PAText color="black" bold className={styles.delivery_method_heading}>
                    {t("Local Pickup")}
                  </PAText>
                  <div className={styles.delivery_sub_heading}>
                    <EstimatedDeliveryDate
                      deliveryMethod={DELIVERY_METHODS.PICKUP}
                      availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                    />
                  </div>
                </div>
                <PAText fontSize={FONT_SIZE[0]} className={`${styles.price_text} ${styles.delivery_method_cost}`}>
                  {freeText}
                </PAText>
              </div>
              <HideIf show={deliveryMethod === DELIVERY_METHODS.PICKUP}>
                <div className={(isShoppingCartPage(pathname)
                  || isCheckoutPages(pathname, query) || (product && isApparelProduct(product))) ? styles.text_container_cart : styles.text_container_product_page}
                >

                  <PickupShippingInfo
                    deliveryMethod={deliveryMethod}
                    availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                  />

                  <DeliveryCounter
                    deliveryMethod={deliveryMethod}
                    availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                  />
                </div>
              </HideIf>
            </>
          )}
        </HideIf>

        <HideIf show={showShippingMethod}>
          <>
            <div
              className={classnames(`${styles.delivery_method_container} ${styles.pointer} ${className}`, {
                [styles.active]: deliveryMethod === DELIVERY_METHODS.SHIPPING,
                [styles.disabled]: showOnlyAvailable && !availableDeliveryMethodsForProducts.includes(DELIVERY_METHODS.SHIPPING),
                [styles.mobile_compact_delivery_method]: compactOnMobile,
                [styles.checkout_delivery_method_container]: isCheckoutPages(pathname, query),
                [styles.delivery_method_container_with_cost_details]: showCostDetailsLayout,

              })}
              onClick={() => handleOnChange(DELIVERY_METHODS.SHIPPING)}
            >
              <div className={styles.icon_wrapper}>
                <DeliveryShippingIcon width={40} height={40} />
              </div>
              <div className={styles.shipping_cost_label_container}>
                <PAText color="black" bold className={styles.delivery_method_heading}>
                  {t("Shipping")}
                </PAText>
                <div className={styles.delivery_sub_heading}>
                  <EstimatedDeliveryDate
                    deliveryMethod={DELIVERY_METHODS.SHIPPING}
                    availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                  />
                  <HideIf show={PRODUCT_PAGE_TYPES.includes(currentPageType) && isSurgeCostApplicable(availableDeliveryMethodsDetails)}>
                    <div className={styles.surcharge}>
                      *
                      {t("Extended Distance Surcharge may apply")}
                    </div>
                  </HideIf>
                </div>
              </div>
              <span className={`d-flex flex-column ${styles.shipping_cost_label} ${styles.delivery_method_cost}`} data-testid="shipping-cost">
                <PAText color="black" fontSize={FONT_SIZE[0]} className={styles.price_text}>
                  {/* {shipmentDetails?.baseShipmentCost ? compactShippingFee(currency(shipmentDetails.baseShipmentCost, locale)) : freeText} */}
                  {freeText}
                </PAText>
                <HideIf show={showShippingCostDetails}>
                  <PAText inlineBlock fontSize={FONT_SIZE[0]} className={styles.shipping_cost_text}>
                    {t("over")}
                    <PAText fontSize={FONT_SIZE[0]} className={styles.bolder_text}>
                      {` ${compactShippingFee(minimumShippingCost(DELIVERY_METHODS.SHIPPING, availableDeliveryMethodsDetails, locale), pathname)} `}
                    </PAText>
                  </PAText>
                </HideIf>
              </span>
            </div>
            <HideIf show={deliveryMethod === DELIVERY_METHODS.SHIPPING}>
              <div className={(isShoppingCartPage(pathname)
                || isCheckoutPages(pathname, query) || (product && isApparelProduct(product))) ? styles.text_container_cart : styles.text_container_product_page}
              >
                <ShippingDeliveryInfo
                  deliveryMethod={deliveryMethod}
                  availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                />
              </div>
            </HideIf>
          </>

        </HideIf>

        <HideIf show={showDeliveryMethod}>

          {deliveryAvailable && (
            <>
              <div
                className={classnames(`${styles.delivery_method_container} ${styles.pointer} ${className}`, {
                  [styles.active]: deliveryMethod === DELIVERY_METHODS.DELIVERY,
                  [styles.disabled]: showOnlyAvailable && !availableDeliveryMethodsForProducts.includes(DELIVERY_METHODS.DELIVERY),
                  [styles.mobile_compact_delivery_method]: compactOnMobile,
                  [styles.checkout_delivery_method_container]: isCheckoutPages(pathname, query),
                  [styles.delivery_method_container_with_cost_details]: showCostDetailsLayout,
                })}
                onClick={() => handleOnChange(DELIVERY_METHODS.DELIVERY)}
              >
                <div className={styles.icon_wrapper}>
                  <DeliveryRapidShippingIcon width={40} height={40} />
                </div>
                <div className={styles.shipping_cost_label_container}>
                  <PAText color="black" bold className={styles.delivery_method_heading}>
                    {t("Rapid Delivery")}
                  </PAText>
                  <div className={styles.delivery_sub_heading}>
                    <EstimatedDeliveryDate
                      deliveryMethod={DELIVERY_METHODS.DELIVERY}
                      availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                    />
                  </div>
                </div>
                <span className={`d-flex flex-column ${styles.shipping_cost_label} ${styles.delivery_method_cost}`} data-testid="delivery-cost">
                  <PAText color="black" fontSize={FONT_SIZE[0]} className={styles.price_text}>
                    {showDeliveryCostDetails ? compactShippingFee(currency(deliveryDetails.deliveryCost, locale), pathname) : freeText}
                  </PAText>
                  <HideIf show={showDeliveryCostDetails}>
                    <PAText inlineBlock fontSize={FONT_SIZE[0]} className={styles.shipping_cost_text}>
                      {t("Free over")}
                      <PAText fontSize={FONT_SIZE[0]} className={styles.bolder_text}>
                        {` ${compactShippingFee(minimumShippingCost(DELIVERY_METHODS.DELIVERY, availableDeliveryMethodsDetails, locale), pathname)} `}
                      </PAText>
                    </PAText>
                  </HideIf>
                </span>
              </div>
              <HideIf show={deliveryMethod === DELIVERY_METHODS.DELIVERY}>
                <div className={(isShoppingCartPage(pathname)
                  || isCheckoutPages(pathname, query) || (product && isApparelProduct(product))) ? styles.text_container_cart : styles.text_container_product_page}
                >
                  <RapidDeliveryInfo
                    deliveryMethod={deliveryMethod}
                    availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
                  />
                </div>
              </HideIf>
            </>

          )}
        </HideIf>
        <HideIf show={isDeliveryMethodUnAvailable(availableDeliveryMethodsDetails)}>
          <PickUpOnlyWarning
            classNames="m-t-20"
          />
        </HideIf>
      </div>
      <div className={`${isShoppingCartPage(pathname)
        || isCheckoutPages(pathname, query) || (product && isApparelProduct(product)) ? `${styles.text_container_next_line_cart} ${styles.text_container_cart_page}` : styles.text_container_next_line_product_page}
      `}
      >
        <ShippingDeliveryInfo
          deliveryMethod={deliveryMethod}
          availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
        />
        <RapidDeliveryInfo
          deliveryMethod={deliveryMethod}
          availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
        />
        <PickupShippingInfo
          deliveryMethod={deliveryMethod}
          availableDeliveryMethodsDetails={availableDeliveryMethodsDetails}
        />
      </div>
    </Card>
  );
};

export default NewDeliveryMethodSelector;
